import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseurl } from "../Baseurl";
import Swal from 'sweetalert2';
import VisitorNavbar from "../component/VisitorNavbar";
import VisitorSidebar from "../component/VisitorSidebar";
import { Modal, Button, Form, Card } from "react-bootstrap";
import { Plus, Edit, Trash2, MapPin } from "react-feather";

const AddressManagement = () => {
  const [loading, setLoading] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const user_id = localStorage.getItem("cust_id");

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    full_name: "",
    phone: "",
    address_line: "",
    city: "",
    state: "",
    pincode: "",
    is_default: false
  });

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}getShippingAddressesByUserId/${user_id}`);
      if (response.data.success) {
        setAddresses(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user_id) fetchAddresses();
  }, [user_id]);

  const handleShowModal = (address = null) => {
    if (address) {
      setEditMode(true);
      setFormData({
        id: address.id,
        full_name: address.full_name,
        phone: address.phone_number || "", 
        address_line: address.address_line1 || "", 
        city: address.city || "",
        state: address.state || "",
        pincode: address.postal_code || "", 
        is_default: address.is_default
      });
    } else {
      setEditMode(false);
      setFormData({ full_name: "", phone: "", address_line: "", city: "", state: "", pincode: "", is_default: false });
    }
    setShowModal(true);
  };

  // Helper to restrict input to numbers only and specific lengths
  const handleNumericInput = (e, field, length) => {
    const value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
    if (value.length <= length) {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Final Validation check
    if (formData.phone.length !== 10) {
      return Swal.fire("Invalid Input", "Phone number must be exactly 10 digits", "warning");
    }
    if (formData.pincode.length !== 6) {
      return Swal.fire("Invalid Input", "Pincode must be exactly 6 digits", "warning");
    }

    try {
      const url = editMode ? `${baseurl}updateAddress/${formData.id}` : `${baseurl}addAddress`;
      const method = editMode ? "put" : "post";
      
      const payload = { ...formData, user_id };
      const response = await axios[method](url, payload);

      if (response.data.success) {
        Swal.fire("Success", `Address ${editMode ? "updated" : "added"} successfully!`, "success");
        setShowModal(false);
        fetchAddresses();
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", error.response?.data?.message || "Action failed", "error");
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Delete address?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${baseurl}deleteAddress/${id}`);
          Swal.fire("Deleted!", "Address removed.", "success");
          fetchAddresses();
        } catch (error) {
          Swal.fire("Error", "Deletion failed", "error");
        }
      }
    });
  };

  return (
    <>
      <VisitorNavbar />
      <div className="container-fluid my-4">
        <div className="row g-3 justify-content-center">
          <div className="col-md-2 hide-on-mobile">
            <VisitorSidebar />
          </div>

          <div className="col-md-10">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="fw-bold m-0"><MapPin size={22} className="me-2 text-primary"/>Address Management</h4>
              <Button variant="primary" className="rounded-pill px-4" onClick={() => handleShowModal()}>
                <Plus size={18} className="me-1" /> Add New
              </Button>
            </div>
            <hr />

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2">Loading addresses...</p>
              </div>
            ) : (
              <div className="row">
                {addresses.map((addr) => (
                  <div className="col-md-6 mb-3" key={addr.id}>
                    <Card className={`h-100 shadow-sm border-0 ${addr.is_default ? 'ring-primary border-start border-primary border-4' : 'border border-light'}`}>
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="fw-bold mb-1">{addr.full_name} 
                              {addr.is_default && <span className="badge bg-primary ms-2 small">Default</span>}
                            </h6>
                            <p className="text-muted small mb-2">{addr.phone_number}</p>
                          </div>
                          <div className="d-flex gap-1">
                            <Button variant="link" className="text-primary p-1" onClick={() => handleShowModal(addr)}><Edit size={16} /></Button>
                            <Button variant="link" className="text-danger p-1" onClick={() => handleDelete(addr.id)}><Trash2 size={16} /></Button>
                          </div>
                        </div>
                        <p className="mb-0 small text-dark">
                          {addr.address_line1}<br/>
                          <span className="text-secondary">{addr.city}, {addr.state} - <strong>{addr.postal_code}</strong></span>
                        </p>
                      </Card.Body>
                    </Card>
                  </div>
                ))}
                {addresses.length === 0 && <div className="text-center py-5 border rounded bg-light text-muted">No saved addresses found. Click "Add New" to start.</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h5 fw-bold">{editMode ? "Edit Address" : "Add New Address"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="px-4">
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold text-uppercase text-muted">Full Name</Form.Label>
              <Form.Control required type="text" placeholder="John Doe" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold text-uppercase text-muted">Phone Number (10 Digits)</Form.Label>
              <Form.Control 
                required 
                type="text" 
                inputMode="numeric"
                placeholder="Enter 10 digit number"
                value={formData.phone} 
                onChange={(e) => handleNumericInput(e, 'phone', 10)} 
              />
              <Form.Text className="text-muted">{formData.phone.length}/10</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold text-uppercase text-muted">Flat, House no., Building, Apartment</Form.Label>
              <Form.Control required as="textarea" rows={2} value={formData.address_line} onChange={(e) => setFormData({...formData, address_line: e.target.value})} />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold text-uppercase text-muted">City</Form.Label>
                  <Form.Control required type="text" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold text-uppercase text-muted">Pincode (6 Digits)</Form.Label>
                  <Form.Control 
                    required 
                    type="text" 
                    inputMode="numeric"
                    placeholder="6 digit code"
                    value={formData.pincode} 
                    onChange={(e) => handleNumericInput(e, 'pincode', 6)} 
                  />
                  <Form.Text className="text-muted">{formData.pincode.length}/6</Form.Text>
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-4">
              <Form.Label className="small fw-bold text-uppercase text-muted">State</Form.Label>
              <Form.Control required type="text" value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} />
            </Form.Group>

            <Form.Check 
              type="switch" 
              id="default-check"
              label="Set as my default shipping address" 
              className="fw-bold text-primary small"
              checked={formData.is_default} 
              onChange={(e) => setFormData({...formData, is_default: e.target.checked})} 
            />
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="white" className="text-muted" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" className="px-4">{editMode ? "Update Address" : "Save Address"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default AddressManagement;